import React, { useContext, useEffect, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { DestinazioneContext } from '../context/DestinazioneContext';
import { FaCalendarAlt, FaFileAlt, FaTable, FaMapMarkerAlt, FaClipboardList, FaBoxes, FaCog, FaRandom } from 'react-icons/fa';
import { MdDarkMode, MdLightMode, MdLogout } from 'react-icons/md';
import { withRememberedCredentialsPreserved } from '../utils/rememberedCredentials';

const SideMenu = ({ onCollapse, setSelectedDay, selectedDay }) => {
  const { darkMode } = useContext(ThemeContext);
  const { destinazione, dayActivationFlags } = useContext(DestinazioneContext);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Stati per il menu
  const [nomeCliente, setNomeCliente] = useState('');
  const [nomeAzienda, setNomeAzienda] = useState('');
  const [isDefaultUser, setIsDefaultUser] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [hasSelectedDestination, setHasSelectedDestination] = useState(false);

  // *** LOGICA SEMPLIFICATA: Carica solo i nomi dal localStorage ***
  useEffect(() => {
    console.log("[SideMenu] Caricamento nomi da localStorage...");
    
    // Carica flag isDefaultUser
    const defaultUserFlag = localStorage.getItem('isDefaultUser');
    const isDefault = defaultUserFlag === 'true';
    
    setIsDefaultUser(isDefault);

    localStorage.setItem('isSuperuser', isDefault ? 'true' : 'false');
    setIsSuperuser(isDefault); // Aggiorna anche lo stato locale se necessario
    
    // Controlla se c'è una destinazione selezionata
    const savedDestinazione = localStorage.getItem('destinazione');
    const hasDestination = !!(savedDestinazione && savedDestinazione !== 'null');
    setHasSelectedDestination(hasDestination);
    console.log("[SideMenu] hasSelectedDestination:", hasDestination);
    
    // Carica informazioni cliente/azienda solo dal localStorage
    if (savedDestinazione) {
      try {
        const dest = JSON.parse(savedDestinazione);
        setNomeCliente(dest.nomeDestinazione || 'Cliente');
      } catch (e) {
        console.error('[SideMenu] Errore parsing destinazione:', e);
        setNomeCliente('Cliente');
      }
    }
    
    // Carica nome azienda
    const companyInfo = localStorage.getItem('companyInfo');
    if (companyInfo) {
      try {
        const company = JSON.parse(companyInfo);
        setNomeAzienda(company.nome_azienda || 'Azienda');
      } catch (e) {
        console.error('[SideMenu] Errore parsing azienda:', e);
        setNomeAzienda('Azienda');
      }
    }

    const applyAdminFlagsFromStorage = () => {
      const isDefault = localStorage.getItem('isDefaultUser') === 'true';
      setIsDefaultUser(isDefault);
      localStorage.setItem('isSuperuser', isDefault ? 'true' : 'false');
      setIsSuperuser(isDefault);
    };

    // Listener per aggiornamenti destinazione
    const handleDestinazioneChanged = () => {
      console.log("[SideMenu] Destinazione cambiata, aggiorno nomi...");
      applyAdminFlagsFromStorage();
      const newDestinazione = localStorage.getItem('destinazione');
      if (newDestinazione) {
        try {
          const dest = JSON.parse(newDestinazione);
          setNomeCliente(dest.nomeDestinazione || 'Cliente');
          setHasSelectedDestination(true);
        } catch (e) {
          console.error('[SideMenu] Errore parsing nuova destinazione:', e);
        }
      } else {
        setHasSelectedDestination(false);
      }
    };

    // Ascolta eventi di cambio destinazione
    window.addEventListener('destinazioneChanged', handleDestinazioneChanged);
    
    return () => {
      window.removeEventListener('destinazioneChanged', handleDestinazioneChanged);
    };
  }, []); // Solo al mount del componente

  const days = [
    { name: 'Lunedì', key: 'lunedi', path: '/ordini/lunedi' },
    { name: 'Martedì', key: 'martedi', path: '/ordini/martedi' },
    { name: 'Mercoledì', key: 'mercoledi', path: '/ordini/mercoledi' },
    { name: 'Giovedì', key: 'giovedi', path: '/ordini/giovedi' },
    { name: 'Venerdì', key: 'venerdi', path: '/ordini/venerdi' },
    { name: 'Sabato', key: 'sabato', path: '/ordini/sabato' },
    { name: 'Domenica', key: 'domenica', path: '/ordini/domenica' },
  ];

  const isDayDisabled = (dayKey) => {
    // Prima prova a usare i dati della destinazione con la logica corretta
    if (destinazione?.datiDestinazione) {
      const dayMapping = {
        'lunedi': 'lun_act',
        'martedi': 'mar_act',
        'mercoledi': 'mer_act',
        'giovedi': 'gio_act',
        'venerdi': 'ven_act',
        'sabato': 'sab_act',
        'domenica': 'dom_act'
      };
      
      const dayField = dayMapping[dayKey];
      if (dayField) {
        const flagValue = destinazione.datiDestinazione[dayField];
        const isDisabled = flagValue === '1'; // CORRETTO: confronto con stringa '1'
       
        return isDisabled;
      }
    }
    
    // Se non abbiamo i dati della destinazione, usa i flag di attivazione
    if (dayActivationFlags && Object.keys(dayActivationFlags).length > 0) {
      const isDisabled = dayActivationFlags[dayKey] === '1'; // CORRETTO: confronto con stringa '1'
      return isDisabled;
    }
    
    // Fallback al sistema precedente
    const value = localStorage.getItem(`dayAllZero_${dayKey}`);
    return value === "1";
  };


  const handleDayClick = (dayKey) => {
    if (location.pathname !== '/home') {
      navigate('/home');
    }
    setSelectedDay(dayKey);
  };

  const logout = () => {
    withRememberedCredentialsPreserved(() => {
      localStorage.clear();
    });
    navigate('/login');
  };

  return (
    <div 
      className={`side-menu ${darkMode ? 'side-menu-dark' : 'side-menu-light'}`}
      style={{
        background: darkMode ? '#2d2a24' : 'linear-gradient(to bottom, #FAF2A1 0%, #ffffff 100%)',
        borderRight: darkMode ? '1px solid #3d3a32' : '1px solid #e8e4c8',
      }}
    >
      {/* Info azienda e cliente */}
      <div className="p-3" style={{ borderBottom: darkMode ? '1px solid #333' : '1px solid #e0e0e0' }}>
        {/* Nome azienda */}
        {nomeAzienda && (
          <div className="mb-3">
            <small className={darkMode ? 'text-secondary' : 'text-muted'}>Azienda:</small>
            <p className={`mb-0 ${darkMode ? 'text-light' : 'text-dark'}`} style={{ 
              fontSize: '16px', 
              fontWeight: 600,
              lineHeight: '1.2',
              wordBreak: 'break-word'
            }}>
              {nomeAzienda}
            </p>
          </div>
        )}
        
        {/* Nome cliente */}
        {nomeCliente && (
          <div>
            <small className={darkMode ? 'text-secondary' : 'text-muted'}>Cliente:</small>
            <p className={`mb-0 ${darkMode ? 'text-light' : 'text-dark'}`} style={{ 
              fontSize: '14px', 
              fontWeight: 500,
              lineHeight: '1.2',
              wordBreak: 'break-word'
            }}>
              {nomeCliente}
            </p>
          </div>
        )}
      </div>

      {/* Pulsante collapse per mobile */}
      {onCollapse && (
        <div className="p-3 d-md-none">
          <button
            className={`btn btn-sm ${darkMode ? 'btn-dark' : 'btn-light'}`}
            onClick={onCollapse}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      <Nav className="flex-column p-3 flex-grow-1">
        
        {/* *** MENU SEMPLIFICATO *** */}
        
        {/* Giorni della settimana - Visibili solo se: 
            1. isDefaultUser = false (utenti normali sempre) 
            2. isDefaultUser = true E hasSelectedDestination = true (admin solo dopo selezione destinazione)
            3. Nascosti quando si è in pagine admin per isDefaultUser = true */}
        {(!isDefaultUser || (isDefaultUser && hasSelectedDestination && !['/destinazioni', '/magazzino', '/preordini', '/smistamento'].includes(location.pathname))) && (
          <div className="mb-4">
            <h6 className={`text-uppercase mb-3 ${darkMode ? 'text-secondary' : 'text-muted'}`} 
                style={{ fontSize: '12px', letterSpacing: '1px' }}>
              Ordini Settimanali
            </h6>
            {days.map((day) => {
              const isSelected = selectedDay === day.key;
              const isDayZero = isDayDisabled(day.key);
              const isSelectedAndZero = isSelected && isDayZero;
              const isSelectedAndNotZero = isSelected && !isDayZero;
              
              return (
                <Nav.Link
                  key={day.key}
                  className={`d-flex align-items-center p-2 my-1 rounded ${
                    !isSelected && isDayZero ? 'text-danger fw-bold' : (darkMode ? 'text-light' : 'text-dark')
                  }`}
                  onClick={() => handleDayClick(day.key)}
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: isSelectedAndZero ? '#dc3545' : 
                                   isSelectedAndNotZero ? '#3d85b8' : '',
                    color: isSelected ? '#ffffff' : 
                           isDayZero ? '#dc3545' : '',
                  }}
                >
                  <FaCalendarAlt 
                    className="me-2" 
                    size={16}
                    style={{ 
                      color: isSelected ? '#ffffff' : 
                             isDayZero ? '#dc3545' : ''
                    }}
                  />
                  <span style={{ fontSize: '14px' }}>{day.name}</span>
                </Nav.Link>
              );
            })}

          <hr className={darkMode ? 'border-secondary' : 'border-light'} />
            <h6 className={`text-uppercase mb-3 ${darkMode ? 'text-secondary' : 'text-muted'}`} 
              style={{ fontSize: '12px', letterSpacing: '1px' }}>
              Gestione Documenti
            </h6>
          {/* Documenti */}
          <Nav.Link
            as={Link}
            to="/documenti"
            className={`d-flex align-items-center p-2 my-1 rounded ${darkMode ? 'text-light' : 'text-dark'} ${
              location.pathname === '/documenti' ? (darkMode ? 'bg-primary' : 'bg-primary text-white') : ''
            }`}
            style={{ transition: 'all 0.2s', fontSize: '14px' }}
          >
            <FaFileAlt className="me-2" size={16} />
            <span>Documenti</span>
          </Nav.Link>
          <Nav.Link
            as={Link}
            to="/documenti/tabella-settimanale"
            className={`d-flex align-items-center p-2 my-1 rounded ${darkMode ? 'text-light' : 'text-dark'} ${
              location.pathname === '/documenti/tabella-settimanale'
                ? darkMode
                  ? 'bg-primary'
                  : 'bg-primary text-white'
                : ''
            }`}
            style={{ transition: 'all 0.2s', fontSize: '14px' }}
          >
            <FaTable className="me-2" size={16} />
            <span>Tabella Settimanale</span>
          </Nav.Link>
          </div>
        )}


        {/* Gestione */}
        <div className="mb-4">  
          {isDefaultUser && ( 
            <>
              <hr className={darkMode ? 'border-secondary' : 'border-light'} />
                <h6 className={`text-uppercase mb-3 ${darkMode ? 'text-secondary' : 'text-muted'}`} 
                  style={{ fontSize: '12px', letterSpacing: '1px' }}>
                  Gestione Amministratore
                </h6>
              </> 
          )}
          
          {/* Destinazioni - Visibile solo per isDefaultUser */}
          {isDefaultUser && (  
            <Nav.Link
              as={Link}
              to="/destinazioni"
              className={`d-flex align-items-center p-2 my-1 rounded ${darkMode ? 'text-light' : 'text-dark'} ${
                location.pathname === '/destinazioni' ? (darkMode ? 'bg-primary' : 'bg-primary text-white') : ''
              }`}
              style={{ transition: 'all 0.2s', fontSize: '14px' }}
            >
              <FaMapMarkerAlt className="me-2" size={16} />
              <span>Destinazioni Clienti</span>
            </Nav.Link>
          )}
          
          {/* Prodotti - Visibile solo per isDefaultUser */}
          {isDefaultUser && (
            <Nav.Link
              as={Link}
              to="/magazzino"
              className={`d-flex align-items-center p-2 my-1 rounded ${darkMode ? 'text-light' : 'text-dark'} ${
                location.pathname === '/magazzino' ? (darkMode ? 'bg-primary' : 'bg-primary text-white') : ''
              }`}
              style={{ transition: 'all 0.2s', fontSize: '14px' }}
            >
              <FaBoxes className="me-2" size={16} />
              <span>Prodotti</span>
            </Nav.Link>
          )}

          {/* Preordini - Visibile solo per isDefaultUser */}
          {isDefaultUser && (
            <Nav.Link
              as={Link}
              to="/preordini"
              className={`d-flex align-items-center p-2 my-1 rounded ${darkMode ? 'text-light' : 'text-dark'} ${
                location.pathname === '/preordini' ? (darkMode ? 'bg-primary' : 'bg-primary text-white') : ''
              }`}
              style={{ transition: 'all 0.2s', fontSize: '14px' }}
            >
              <FaClipboardList className="me-2" size={16} />
              <span>Preordini</span>
            </Nav.Link>
          )}

          {/* Smistamento - Visibile solo per isDefaultUser */}
          {isDefaultUser && (
            <Nav.Link
              as={Link}
              to="/smistamento"
              className={`d-flex align-items-center p-2 my-1 rounded ${darkMode ? 'text-light' : 'text-dark'} ${
                location.pathname === '/smistamento' ? (darkMode ? 'bg-primary' : 'bg-primary text-white') : ''
              }`}
              style={{ transition: 'all 0.2s', fontSize: '14px' }}
            >
              <FaRandom className="me-2" size={16} />
              <span>Smistamento</span>
            </Nav.Link>
          )}

          {/* Impostazioni Amministratore - Visibile solo per isDefaultUser */}
          {isDefaultUser && (
            <Nav.Link
              as={Link}
              to="/admin-settings"
              className={`d-flex align-items-center p-2 my-1 rounded ${darkMode ? 'text-light' : 'text-dark'} ${
                location.pathname === '/admin-settings' ? (darkMode ? 'bg-primary' : 'bg-primary text-white') : ''
              }`}
              style={{ transition: 'all 0.2s', fontSize: '14px' }}
            >
              <FaCog className="me-2" size={16} />
              <span>Impostazioni Sistema</span>
            </Nav.Link>
          )}


        </div>
      </Nav>

    </div>
  );
};

export default SideMenu;